/**
 * AVBP project file store — the team document/upload layer.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (AVBP_FLOW_STATUS 2026-05-19 §C.3 — file upload is the hard gap:
 *  "without an upload control, files cannot enter the system")
 * ─────────────────────────────────────────────────────────────────────────
 *  The Firebase Storage SDK was imported (firebase.ts `storage`) but never
 *  used; the 📎 button only toasted "lands with the Storage sprint". This
 *  module wires REAL upload: bytes → Firebase Storage, metadata →
 *  Firestore, so the list renders and files are retrievable/downloadable.
 *
 *  PATHS (BOTH already permitted by the LIVE rules — NO rules change):
 *    • Storage:   projects/{pid}/artefacts/{fileId}/{safeName}
 *      storage.rules already has:
 *        match /projects/{projectId}/artefacts/{fileId}/{fileName} {
 *          allow read:   if request.auth != null;
 *          allow create: if request.auth != null && size<25MB
 *                          && (image/* | application/pdf | application/*);
 *          allow delete: if owner-email;
 *        }
 *      → an authenticated team session can upload + read. Delete is
 *        owner-email-only at the Storage layer, so this module does a
 *        SOFT-ARCHIVE of the metadata (consistent with projectStore's
 *        archive pattern; the bytes are orphaned, never a hard fail).
 *    • Firestore: projects/{pid}/artefacts/{fileId}
 *      firestore.rules already has:
 *        match /projects/{projectId}/artefacts/{fileId} {
 *          allow read:   if isTeam() || (isClient owns project);
 *          allow create: if isTeam();
 *          allow update: if isTeam();   allow delete: if false;
 *        }
 *      → isTeam() create/update; NO delete by rule (delete:false) — hence
 *        archive-not-delete, exactly like projectStore.archiveProject.
 *
 *  ACCESS: team-only in practice — the Files UI lives on ProjectDetail
 *  (route guard roles=['admin','staff']); the rules independently enforce
 *  isTeam() (client read is allowed by rule for their own project but the
 *  client has no Files UI in this sprint).
 *
 *  PURE/IMPURE split (mirrors projectStore.ts / staffRoles.ts):
 *  `safeFileName`, `fileKindOf`, `artefactDocBody`, `fileRowFrom`,
 *  `humanSize`, `isAcceptedType` are PURE + unit-tested. Only
 *  `uploadProjectFile` / `listenProjectFiles` / `archiveProjectFile` touch
 *  Firebase.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from './firebase';

/* ════════════════════ Accepted types (mirror storage.rules) ════════════════════ */

/**
 * The Storage rule allows image/*, application/pdf and application/* (≤25MB).
 * We mirror that ceiling client-side so a reject is a friendly message, not
 * an opaque Storage 403. 25MB hard cap (the rule's number).
 */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

export type FileKind = 'image' | 'pdf' | 'doc' | 'other';

/** PURE — coarse kind from a MIME type (drives the list icon/label). */
export function fileKindOf(contentType: string): FileKind {
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('image/')) return 'image';
  if (t === 'application/pdf') return 'pdf';
  if (
    t.startsWith('application/') ||
    t.startsWith('text/') ||
    t.includes('word') ||
    t.includes('sheet') ||
    t.includes('excel')
  ) {
    return 'doc';
  }
  return 'other';
}

/**
 * PURE — is this MIME type accepted by the live Storage rule? image/* and
 * application/pdf and application/* are. text/* is wrapped into
 * application/* by browsers inconsistently, so we accept it too only when
 * it is a known doc-ish type — but the AUTHORITATIVE gate is the Storage
 * rule; this is just the friendly pre-check.
 */
export function isAcceptedType(contentType: string): boolean {
  const t = (contentType || '').toLowerCase();
  // NOTE: text/csv + text/plain are intentionally NOT accepted here — the
  // authoritative Storage rule for projects/{pid}/artefacts/ allows only
  // image/* + application/* + pdf, so a text/* upload would fail with a
  // misleading error. Re-enable ONLY together with a matching `text/.*`
  // Storage-rule allowance (verified against the LIVE ruleset).
  return (
    t.startsWith('image/') ||
    t === 'application/pdf' ||
    t.startsWith('application/')
  );
}

/* ════════════════════ Pure name / size helpers ════════════════════ */

/**
 * PURE — make a storage-safe file name. Keeps the extension, strips path
 * separators + control chars, collapses whitespace, caps length. Never
 * empty (falls back to 'file'). The fileId (caller-minted) guarantees
 * uniqueness, so this only needs to be SAFE, not unique.
 */
export function safeFileName(raw: string): string {
  const cleaned = (raw || '')
    .replace(/[\\/]/g, '_')
    // strip ASCII control chars (0x00-0x1F and 0x7F)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return cleaned || 'file';
}

/** PURE — human-readable size (B / KB / MB) for the list row. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ════════════════════ Pure Firestore doc body ════════════════════ */

export interface ArtefactSeed {
  fileId: string;
  fileName: string;
  contentType: string;
  size: number;
  storagePath: string;
  downloadURL: string;
  uploaderUid: string;
  uploaderName: string;
}

/**
 * PURE — the EXACT `projects/{pid}/artefacts/{fileId}` doc body. Only the
 * bounded metadata set + an audit stamp. No field outside this list can be
 * smuggled (so the rules-allowed create stays minimal). `uploadedAt` is
 * filled with serverTimestamp by the impure caller, not here, so this is
 * unit-testable.
 */
export function artefactDocBody(
  seed: ArtefactSeed,
): Record<string, unknown> {
  return {
    fileId: seed.fileId,
    fileName: seed.fileName,
    contentType: seed.contentType,
    size: seed.size,
    kind: fileKindOf(seed.contentType),
    storagePath: seed.storagePath,
    downloadURL: seed.downloadURL,
    uploaderUid: seed.uploaderUid,
    uploaderName: seed.uploaderName,
    archived: false,
    source: 'portal-file-upload',
  };
}

/* ════════════════════ File row model ════════════════════ */

export interface ProjectFile {
  fileId: string;
  fileName: string;
  contentType: string;
  size: number;
  kind: FileKind;
  storagePath: string;
  downloadURL: string;
  uploaderName: string;
  /** soft-archived (rules forbid hard delete) — hidden from default list. */
  archived: boolean;
}

/** PURE — map a raw artefact doc to a ProjectFile (defensive defaults). */
export function fileRowFrom(
  id: string,
  d: Record<string, unknown>,
): ProjectFile {
  const contentType = (d.contentType as string) ?? '';
  return {
    fileId: (d.fileId as string) ?? id,
    fileName: (d.fileName as string) ?? id,
    contentType,
    size: typeof d.size === 'number' ? (d.size as number) : 0,
    kind: (d.kind as FileKind) ?? fileKindOf(contentType),
    storagePath: (d.storagePath as string) ?? '',
    downloadURL: (d.downloadURL as string) ?? '',
    uploaderName: (d.uploaderName as string) ?? 'Unknown',
    archived: d.archived === true,
  };
}

/* ════════════════════ Firestore + Storage seam (impure) ════════════════════ */

/** Mint a stable artefact id (time-ordered + random — no extra dep). */
function mintFileId(): string {
  return `f_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * THE upload. (1) validate type+size client-side (friendly reject),
 * (2) upload bytes to Storage projects/{pid}/artefacts/{fileId}/{name},
 * (3) get a download URL, (4) write the artefact metadata doc. Both paths
 * are isTeam()/auth-allowed by the LIVE rules — no rules change. Returns
 * the new fileId. Throws a readable Error on a rejected type / oversize /
 * transport failure (the caller toasts it).
 */
export async function uploadProjectFile(
  pid: string,
  file: File,
  uploader: { uid: string; name: string },
): Promise<string> {
  if (!isAcceptedType(file.type)) {
    throw new Error(
      `"${file.type || 'unknown type'}" is not an accepted file type ` +
        `(images, PDF and documents only).`,
    );
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `File is ${humanSize(file.size)} — the limit is ${humanSize(
        MAX_FILE_BYTES,
      )}.`,
    );
  }
  const fileId = mintFileId();
  const fileName = safeFileName(file.name);
  const storagePath = `projects/${pid}/artefacts/${fileId}/${fileName}`;

  const sRef = storageRef(storage, storagePath);
  await uploadBytes(sRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  const downloadURL = await getDownloadURL(sRef);

  await setDoc(doc(db, 'projects', pid, 'artefacts', fileId), {
    ...artefactDocBody({
      fileId,
      fileName,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
      storagePath,
      downloadURL,
      uploaderUid: uploader.uid,
      uploaderName: uploader.name,
    }),
    uploadedAt: serverTimestamp(),
  });
  return fileId;
}

/**
 * Live realtime list of a project's files. isTeam() read is rules-allowed;
 * the Files UI is route-gated to admin/staff. Archived files are filtered
 * out of the default view by the caller.
 */
export function listenProjectFiles(
  pid: string,
  cb: (files: ProjectFile[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'projects', pid, 'artefacts')),
    (snap) => {
      const rows: ProjectFile[] = [];
      for (const dd of snap.docs) rows.push(fileRowFrom(dd.id, dd.data()));
      cb(rows);
    },
    (e) => onErr?.(e),
  );
}

/**
 * Soft-archive (or restore) a file's metadata. The Firestore rule is
 * `delete: if false` (no hard delete — same audit discipline as
 * projectStore.archiveProject) and the Storage delete rule is
 * owner-email-only, so "remove" = a rules-allowed isTeam() `archived` flag
 * update. Reversible. Confirm-gated at the call site.
 */
export async function archiveProjectFile(
  pid: string,
  fileId: string,
  actorName: string,
  archived = true,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid, 'artefacts', fileId), {
    archived,
    archivedBy: actorName,
    updatedAt: serverTimestamp(),
  });
}
